// =============================================================================
// ACT READING TEST BANK - Form 25MC1
// Source: Official ACT Prep Guide 2025-2026
// Total Questions: 36 (27 scored, 9 field test)
// =============================================================================

export const formId = '25MC1';
export const subject = 'reading';
export const totalQuestions = 36;
export const scoredQuestions = 27;
export const timeLimit = 40; // minutes

// Field test questions (not scored) - Questions 1-9
export const fieldTestPositions = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// -----------------------------------------------------------------------------
// SCORING TABLE (27 scored questions)
// -----------------------------------------------------------------------------
export const scoringTable = {
  27: 36, 26: 35, 25: 34, 24: 32, 23: 30, 22: 28, 21: 26, 20: 25, 19: 24, 18: 23,
  17: 22, 16: 21, 15: 20, 14: 18, 13: 17, 12: 16, 11: 15, 10: 14, 9: 13, 8: 12,
  7: 12, 6: 11, 5: 10, 4: 9, 3: 7, 2: 5, 1: 3, 0: 1
};

// -----------------------------------------------------------------------------
// CATEGORY DEFINITIONS
// -----------------------------------------------------------------------------
export const categories = {
  KID: { name: 'Key Ideas & Details', description: 'Central ideas, summarizing, inferences, relationships', count: 13 },
  CS: { name: 'Craft & Structure', description: 'Word meaning, author\'s purpose, text structure', count: 9 },
  IKI: { name: 'Integration of Knowledge & Ideas', description: 'Claims, evidence, multiple texts', count: 5 }
};

// -----------------------------------------------------------------------------
// PASSAGES
// -----------------------------------------------------------------------------
export const passages = [
  {
    id: 'R1',
    title: 'Janus',
    author: 'Ann Beattie',
    type: 'literary-narrative',
    genre: 'Short Story',
    source: '©1985 by The New Yorker Magazine, Inc.',
    questionRange: [1, 9],
    description: 'A real estate agent named Andrea has a special bowl that she uses when showing houses.'
  },
  {
    id: 'R2',
    title: 'The Botany of Desire: A Plant\'s-Eye View of the World',
    author: 'Michael Pollan',
    type: 'informational',
    genre: 'Science/History',
    source: '©2001 by Michael Pollan',
    questionRange: [10, 18],
    description: 'About the broken tulip phenomenon in 17th century Holland and the virus that caused it.'
  },
  {
    id: 'R3',
    title: 'Paired Passages on Hip-Hop',
    author: 'Joseph G. Schloss / Steve Stoute',
    type: 'paired-informational',
    genre: 'Cultural Studies/Memoir',
    source: 'Passage A: ©2009 by Oxford University Press; Passage B: ©2011 by Steve Stoute',
    questionRange: [19, 27],
    description: 'Two perspectives on hip-hop culture and music.'
  },
  {
    id: 'R4',
    title: 'The Rise and Fall of the Living Fossil',
    author: 'Ferris Jabr',
    type: 'informational',
    genre: 'Science',
    source: '©2015 by Nautilus',
    questionRange: [28, 36],
    description: 'About research that challenged the "living fossil" theory of crocodilian evolution.'
  }
];

// -----------------------------------------------------------------------------
// QUESTIONS
// -----------------------------------------------------------------------------
export const questions = [
  // PASSAGE I: Janus - Literary Narrative (Q1-9) - FIELD TEST
  {
    id: '25MC1-R01',
    formId: '25MC1',
    questionNumber: 1,
    passageId: 'R1',
    category: 'CS',
    subcategory: 'Point of View',
    difficulty: 2,
    isScored: false,
    question: 'The point of view from which the passage is told is best described as that of a:',
    choices: [
      { letter: 'A', text: 'first person narrator, present in the action, who relates events as they happen.' },
      { letter: 'B', text: 'first person narrator, not present in the action, who relates events that happened in the past.' },
      { letter: 'C', text: 'third person narrator, present in the action, who relates the thoughts and feelings of many characters.' },
      { letter: 'D', text: 'third person narrator, not present in the action, who relates the thoughts and feelings of primarily one character.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'The passage uses third person narration focused on Andrea\'s perspective and inner thoughts.'
  },
  {
    id: '25MC1-R02',
    formId: '25MC1',
    questionNumber: 2,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Main Idea',
    difficulty: 2,
    isScored: false,
    question: 'The passage as a whole can best be described as an exploration of the:',
    choices: [
      { letter: 'F', text: 'career of a real estate agent and the agent\'s typically mundane transactions with clients.' },
      { letter: 'G', text: 'special glaze on a bowl and why the glaze makes the bowl both subtle and noticeable.' },
      { letter: 'H', text: 'perceived perfection of an object and that object\'s effect on people.' },
      { letter: 'J', text: 'problems that can result from a person\'s unyielding focus on obtaining material goods.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The passage explores Andrea\'s relationship with her "perfect" bowl and how it affects her and others.'
  },
  {
    id: '25MC1-R03',
    formId: '25MC1',
    questionNumber: 3,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Inference',
    difficulty: 2,
    isScored: false,
    question: 'The passage most strongly suggests that a useful characteristic of the bowl, in terms of Andrea\'s purpose for the object, is the bowl\'s:',
    choices: [
      { letter: 'A', text: 'universal appeal.' },
      { letter: 'B', text: 'famous designer.' },
      { letter: 'C', text: 'ostentatious look.' },
      { letter: 'D', text: 'commercial availability.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The passage states the bowl appeals to people with different tastes - both those who like country furniture and those who like opulence.'
  },
  {
    id: '25MC1-R04',
    formId: '25MC1',
    questionNumber: 4,
    passageId: 'R1',
    category: 'CS',
    subcategory: 'Characterization',
    difficulty: 2,
    isScored: false,
    question: 'In lines 53-75, Andrea responds to an inquiry about her bowl and explains why her bowl was placed in a client\'s home with statements that can best be described as:',
    choices: [
      { letter: 'F', text: 'vague generalizations.' },
      { letter: 'G', text: 'absolute truths.' },
      { letter: 'H', text: 'half-truths.' },
      { letter: 'J', text: 'lies.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Andrea lies about not knowing what bowl the woman is referring to and later lies about how she got the bowl.'
  },
  {
    id: '25MC1-R05',
    formId: '25MC1',
    questionNumber: 5,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Comparison',
    difficulty: 2,
    isScored: false,
    question: 'In the passage, Andrea is characterized as believing that compared to most tricks used by real estate agents to impress potential buyers, her trick of placing the bowl in a home is:',
    choices: [
      { letter: 'A', text: 'more humorous to potential buyers.' },
      { letter: 'B', text: 'more obvious to potential buyers.' },
      { letter: 'C', text: 'less familiar to potential buyers.' },
      { letter: 'D', text: 'less enticing to potential buyers.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The passage contrasts obvious tricks like fires and flowers with the bowl\'s subtle, unfamiliar appeal.'
  },
  {
    id: '25MC1-R06',
    formId: '25MC1',
    questionNumber: 6,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: false,
    question: 'According to the passage, the random placement of colors in the bowl\'s glaze creates a surface that:',
    choices: [
      { letter: 'F', text: 'acts as a mirror.' },
      { letter: 'G', text: 'seems to move.' },
      { letter: 'H', text: 'appears cracked in the sunlight.' },
      { letter: 'J', text: 'scatters prisms on the walls of a room.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The passage states "Something about the colors and their random placement suggested motion."'
  },
  {
    id: '25MC1-R07',
    formId: '25MC1',
    questionNumber: 7,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Main Idea',
    difficulty: 2,
    isScored: false,
    question: 'One main point of the fifth paragraph (lines 53-62) is that:',
    choices: [
      { letter: 'A', text: 'Andrea\'s bowl sometimes attracts more interest than does the house itself.' },
      { letter: 'B', text: 'Andrea\'s bowl does not actually belong to her, but she hopes to find its owner.' },
      { letter: 'C', text: 'Andrea is often asked about the bowl when a client puts in an offer on a house.' },
      { letter: 'D', text: 'Andrea sometimes forgets where in a house she has placed the bowl.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The woman who called hadn\'t even put in an offer on the house but wanted to know about the bowl.'
  },
  {
    id: '25MC1-R08',
    formId: '25MC1',
    questionNumber: 8,
    passageId: 'R1',
    category: 'CS',
    subcategory: 'Comparison',
    difficulty: 2,
    isScored: false,
    question: 'In the passage, the admiration the bowl receives is directly compared to the admiration received by:',
    choices: [
      { letter: 'F', text: 'a mutt.' },
      { letter: 'G', text: 'a plastic frog.' },
      { letter: 'H', text: 'a cherry table.' },
      { letter: 'J', text: 'the aroma of spring.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The passage compares the bowl to "a mutt who has no reason to suspect he might be funny."'
  },
  {
    id: '25MC1-R09',
    formId: '25MC1',
    questionNumber: 9,
    passageId: 'R1',
    category: 'KID',
    subcategory: 'Inference',
    difficulty: 2,
    isScored: false,
    question: 'The passage suggests that one reason prospective home buyers have difficulty sharing their thoughts about the bowl is that they realize:',
    choices: [
      { letter: 'A', text: 'they are not visiting the home for the purpose of noticing decorative objects.' },
      { letter: 'B', text: 'they do not want to reveal that they have the financial means to buy the bowl.' },
      { letter: 'C', text: 'Andrea might start talking about the bowl instead of discussing the home that is for sale.' },
      { letter: 'D', text: 'Andrea might find the bowl even more intriguing than they do.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The passage states "Perhaps it was because they were in the house for a serious reason, not to notice some object."'
  },

  // PASSAGE II: The Botany of Desire (Q10-18) - SCORED
  {
    id: '25MC1-R10',
    formId: '25MC1',
    questionNumber: 10,
    passageId: 'R2',
    category: 'CS',
    subcategory: 'Main Purpose',
    difficulty: 2,
    isScored: true,
    question: 'The main purpose of the passage is to:',
    choices: [
      { letter: 'F', text: 'highlight changes in the flower industry from the seventeenth century through today.' },
      { letter: 'G', text: 'examine the way certain plants have been represented in art over the centuries.' },
      { letter: 'H', text: 'provide an overview of plant viruses and the way they affect the flower market.' },
      { letter: 'J', text: 'explain a particular flower variation and how it has been perceived historically.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The passage explains the broken tulip phenomenon and how it was valued historically and then eliminated.'
  },
  {
    id: '25MC1-R11',
    formId: '25MC1',
    questionNumber: 11,
    passageId: 'R2',
    category: 'KID',
    subcategory: 'Main Idea',
    difficulty: 2,
    isScored: true,
    question: 'The main point of the second paragraph (lines 21-36) is that:',
    choices: [
      { letter: 'A', text: 'modern Rembrandt tulips have been painted by many of today\'s most famous artists.' },
      { letter: 'B', text: 'compared to seventeenth-century broken tulips, today\'s multicolored tulips are less visually appealing.' },
      { letter: 'C', text: 'the tulip break known as Semper Augustus was a striking example of the seventeenth-century broken tulip.' },
      { letter: 'D', text: 'Rembrandt was responsible for painting the most famous tulip breaks of his time.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The paragraph states modern Rembrandts "look clumsy by comparison" to original broken tulips.'
  },
  {
    id: '25MC1-R12',
    formId: '25MC1',
    questionNumber: 12,
    passageId: 'R2',
    category: 'KID',
    subcategory: 'Inference',
    difficulty: 2,
    isScored: true,
    question: 'It can reasonably be inferred from the passage that some seventeenth-century tulip growers believed tulip breaks were mainly caused by:',
    choices: [
      { letter: 'F', text: 'suppliers\' storage conditions.' },
      { letter: 'G', text: 'diseased tulip bulbs.' },
      { letter: 'H', text: 'certain growing techniques.' },
      { letter: 'J', text: 'certain weather patterns.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'Growers tried various techniques like sprinkling paint powder and using pigeon droppings, believing these caused breaks.'
  },
  {
    id: '25MC1-R13',
    formId: '25MC1',
    questionNumber: 13,
    passageId: 'R2',
    category: 'CS',
    subcategory: 'Function',
    difficulty: 2,
    isScored: true,
    question: 'The information in lines 57-64 primarily functions to:',
    choices: [
      { letter: 'A', text: 'describe the range of potential tulip colors.' },
      { letter: 'B', text: 'explain how the color variation in a broken tulip occurs.' },
      { letter: 'C', text: 'argue that yellow and white are the only natural tulip colors.' },
      { letter: 'D', text: 'indicate why broken tulips contain no anthocyanin.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'This section explains the two-pigment system and how the virus suppresses anthocyanin to create the patterns.'
  },
  {
    id: '25MC1-R14',
    formId: '25MC1',
    questionNumber: 14,
    passageId: 'R2',
    category: 'CS',
    subcategory: 'Structure',
    difficulty: 2,
    isScored: true,
    question: 'The sixth paragraph (lines 79-85) differs from the rest of the passage in that it:',
    choices: [
      { letter: 'F', text: 'questions whether the virus that caused broken tulips was harmful to bulbs.' },
      { letter: 'G', text: 'argues that growers should have dealt with broken tulips differently.' },
      { letter: 'H', text: 'challenges the idea that broken tulips were beautiful.' },
      { letter: 'J', text: 'presents a personal meditation on broken tulips.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The paragraph begins with "I can\'t help thinking" showing the author\'s personal reflection.'
  },
  {
    id: '25MC1-R15',
    formId: '25MC1',
    questionNumber: 15,
    passageId: 'R2',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: true,
    question: 'According to the passage, in the seventeenth century, the fact that broken tulip bulbs tended to produce fewer and smaller offsets compared to typical tulip bulbs resulted in:',
    choices: [
      { letter: 'A', text: 'a decrease in the demand for broken tulips.' },
      { letter: 'B', text: 'a fear among growers that broken tulips were diseased.' },
      { letter: 'C', text: 'an increase in prices for broken tulips.' },
      { letter: 'D', text: 'a desire among growers to plant a wider variety of crops.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The passage states that fewer offsets "drove their prices still higher."'
  },
  {
    id: '25MC1-R16',
    formId: '25MC1',
    questionNumber: 16,
    passageId: 'R2',
    category: 'KID',
    subcategory: 'Comparison',
    difficulty: 2,
    isScored: true,
    question: 'In the passage, the author compares broken tulips as they are represented in Rembrandt\'s paintings to:',
    choices: [
      { letter: 'F', text: 'peach-tree blossoms.' },
      { letter: 'G', text: 'paint powders sprinkled on the ground.' },
      { letter: 'H', text: 'a painting hastily done with a thick brush.' },
      { letter: 'J', text: 'intricately marbleized papers.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The passage compares the petals of broken tulips to "marbleized papers" with "extravagant swirls of color."'
  },
  {
    id: '25MC1-R17',
    formId: '25MC1',
    questionNumber: 17,
    passageId: 'R2',
    category: 'CS',
    subcategory: 'Purpose',
    difficulty: 2,
    isScored: true,
    question: 'The passage author most likely mentions that peach trees were a staple of seventeenth-century gardens to:',
    choices: [
      { letter: 'A', text: 'highlight a crop favored by growers who did not cultivate tulips.' },
      { letter: 'B', text: 'emphasize that peach trees are not as popular in gardens today.' },
      { letter: 'C', text: 'explain how peach potato aphids spread the tulip virus.' },
      { letter: 'D', text: 'compare tulips to another popular seventeenth-century crop.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The mention of peach trees explains how the peach potato aphid (which spread the virus) had access to tulips.'
  },
  {
    id: '25MC1-R18',
    formId: '25MC1',
    questionNumber: 18,
    passageId: 'R2',
    category: 'CS',
    subcategory: 'Vocabulary',
    difficulty: 2,
    isScored: true,
    question: 'As it is used in line 80, the word abandon most nearly means:',
    choices: [
      { letter: 'F', text: 'uninhibitedness.' },
      { letter: 'G', text: 'relinquishment.' },
      { letter: 'H', text: 'retreat.' },
      { letter: 'J', text: 'denial.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'In context, "abandon" refers to the wild, uninhibited quality the virus added to the tulip\'s formal appearance.'
  },

  // PASSAGE III: Paired Passages on Hip-Hop (Q19-27) - SCORED
  {
    id: '25MC1-R19',
    formId: '25MC1',
    questionNumber: 19,
    passageId: 'R3',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: true,
    question: 'According to Passage A, one reason elements of hip-hop culture such as b-boying are rarely represented in mass media is that these art forms:',
    choices: [
      { letter: 'A', text: 'have never been brought to the public\'s attention.' },
      { letter: 'B', text: 'are not bought and sold as products.' },
      { letter: 'C', text: 'do not appeal to young people.' },
      { letter: 'D', text: 'declined in popularity after the 1970s.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Passage A states b-boying was "an advertisement with no product."'
  },
  {
    id: '25MC1-R20',
    formId: '25MC1',
    questionNumber: 20,
    passageId: 'R3',
    category: 'CS',
    subcategory: 'Vocabulary',
    difficulty: 2,
    isScored: true,
    question: 'As it is used in line 38, the word sensibilities most nearly means:',
    choices: [
      { letter: 'F', text: 'emotions.' },
      { letter: 'G', text: 'sensitivities.' },
      { letter: 'H', text: 'perspectives.' },
      { letter: 'J', text: 'feelings of gratitude.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'In context, "sensibilities" refers to the different perspectives or approaches of hip-hop culture and the music industry.'
  },
  {
    id: '25MC1-R21',
    formId: '25MC1',
    questionNumber: 21,
    passageId: 'R3',
    category: 'KID',
    subcategory: 'Relationship',
    difficulty: 2,
    isScored: true,
    question: 'Based on Passage A, which statement best captures the relationship between Tin Pan Alley pop music and rap music?',
    choices: [
      { letter: 'A', text: 'Rap artists have rejected every aspect of Tin Pan Alley pop.' },
      { letter: 'B', text: 'Rap artists have been aware of Tin Pan Alley pop but not influenced by it.' },
      { letter: 'C', text: 'Tin Pan Alley pop developed at the same time as rap.' },
      { letter: 'D', text: 'Tin Pan Alley pop has influenced many rap artists.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Passage A states rap maintains "a strict 16-bar verse structure derived from Tin Pan Alley pop music."'
  },
  {
    id: '25MC1-R22',
    formId: '25MC1',
    questionNumber: 22,
    passageId: 'R3',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following details does the author of Passage B highlight as one that caused "Rapper\'s Delight" to stand out as different compared to other songs he knew?',
    choices: [
      { letter: 'F', text: 'The song\'s intro' },
      { letter: 'G', text: 'The female vocals' },
      { letter: 'H', text: 'The length of the song' },
      { letter: 'J', text: 'The fact that the song was on a vinyl record' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The author emphasizes the song was "Almost fifteen minutes long" - much longer than typical songs.'
  },
  {
    id: '25MC1-R23',
    formId: '25MC1',
    questionNumber: 23,
    passageId: 'R3',
    category: 'KID',
    subcategory: 'Main Idea',
    difficulty: 2,
    isScored: true,
    question: 'In the context of Passage B, the main point of the third paragraph (lines 62-76) is that the author was:',
    choices: [
      { letter: 'A', text: 'struck by the combination of new and established musical elements in the music he was hearing.' },
      { letter: 'B', text: 'uncomfortable with what he viewed as an unwelcome change to a favorite song.' },
      { letter: 'C', text: 'more interested in an unfamiliar album label than in the new music that was playing.' },
      { letter: 'D', text: 'convinced that the new form of music he was hearing would become more popular than disco.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The paragraph describes recognizing "Good Times" but hearing something "totally different" - a combination of familiar and new.'
  },
  {
    id: '25MC1-R24',
    formId: '25MC1',
    questionNumber: 24,
    passageId: 'R3',
    category: 'KID',
    subcategory: 'Inference',
    difficulty: 2,
    isScored: true,
    question: 'Based on Passage B, it can reasonably be inferred that the author views his first exposure to rap music as:',
    choices: [
      { letter: 'F', text: 'memorable but ultimately not very important.' },
      { letter: 'G', text: 'significant for his childhood but less so for his adulthood.' },
      { letter: 'H', text: 'a transformative experience.' },
      { letter: 'J', text: 'a disappointing experience.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: 'The author says he heard "a record that changed my life (and pop culture) forever."'
  },
  {
    id: '25MC1-R25',
    formId: '25MC1',
    questionNumber: 25,
    passageId: 'R3',
    category: 'IKI',
    subcategory: 'Comparison',
    difficulty: 2,
    isScored: true,
    question: 'Compared to Passage A, Passage B focuses more on:',
    choices: [
      { letter: 'A', text: 'early hip-hop\'s interaction with the marketplace.' },
      { letter: 'B', text: 'attempts to move hip-hop art into galleries.' },
      { letter: 'C', text: 'the mass media.' },
      { letter: 'D', text: 'the author\'s personal experience.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Passage B is a personal memoir about the author\'s first encounter with rap, while Passage A is more analytical.'
  },
  {
    id: '25MC1-R26',
    formId: '25MC1',
    questionNumber: 26,
    passageId: 'R3',
    category: 'IKI',
    subcategory: 'Comparison',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following elements of Passage B is not included in Passage A?',
    choices: [
      { letter: 'F', text: 'A story involving a particular rap song' },
      { letter: 'G', text: 'A discussion of the early days of hip-hop' },
      { letter: 'H', text: 'A mention of the New York City area in the context of hip-hop' },
      { letter: 'J', text: 'An acknowledgment of rap\'s interaction with other musical forms' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'Only Passage B tells a specific story about hearing "Rapper\'s Delight."'
  },
  {
    id: '25MC1-R27',
    formId: '25MC1',
    questionNumber: 27,
    passageId: 'R3',
    category: 'IKI',
    subcategory: 'Synthesis',
    difficulty: 2,
    isScored: true,
    question: 'The authors of both passages would most likely agree with the idea that early rap music:',
    choices: [
      { letter: 'A', text: 'represented artists\' rejection of the music industry and its practices.' },
      { letter: 'B', text: 'represented a significant development in American popular culture.' },
      { letter: 'C', text: 'was more popular than today\'s rap music.' },
      { letter: 'D', text: 'was slow to find an audience.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'Both passages treat early rap as a culturally significant development, though they approach it differently.'
  },

  // PASSAGE IV: The Rise and Fall of the Living Fossil (Q28-36) - SCORED
  {
    id: '25MC1-R28',
    formId: '25MC1',
    questionNumber: 28,
    passageId: 'R4',
    category: 'IKI',
    subcategory: 'Evidence',
    difficulty: 2,
    isScored: true,
    question: 'In the context of the passage, how does the analysis of crocodilian immune system molecules relate to the living fossil theory of crocodilian evolution?',
    choices: [
      { letter: 'F', text: 'The analysis confirms the living fossil theory.' },
      { letter: 'G', text: 'The analysis suggests the living fossil theory is accurate.' },
      { letter: 'H', text: 'The analysis supports the living fossil theory in some ways and does not support the theory in other ways.' },
      { letter: 'J', text: 'The analysis does not support the living fossil theory.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'The molecular analysis showing similarity contradicted the living fossil theory\'s prediction of substantial differences.'
  },
  {
    id: '25MC1-R29',
    formId: '25MC1',
    questionNumber: 29,
    passageId: 'R4',
    category: 'KID',
    subcategory: 'Summary',
    difficulty: 2,
    isScored: true,
    question: 'Which of the following statements best summarizes Oaks\'s analysis of Nile crocodiles\' DNA as it is presented in the third paragraph (lines 31-42)?',
    choices: [
      { letter: 'A', text: 'It suggested that Nile crocodiles are older than what was previously believed, which does not support the living fossil theory of crocodiles.' },
      { letter: 'B', text: 'It suggested that different species of crocodiles do not share a basal ancestor, which the scientific community has confirmed.' },
      { letter: 'C', text: 'It suggested that the analysis was hastily done, which prompted the scientific community to ignore it.' },
      { letter: 'D', text: 'It suggested that the DNA came from two species, which did not support the living fossil theory of crocodiles.' }
    ],
    correctIndex: 3,
    correctLetter: 'D',
    explanation: 'Oaks found variation suggesting two species, and living fossils are not supposed to continue speciating.'
  },
  {
    id: '25MC1-R30',
    formId: '25MC1',
    questionNumber: 30,
    passageId: 'R4',
    category: 'CS',
    subcategory: 'Purpose',
    difficulty: 2,
    isScored: true,
    question: 'The main purpose of the fifth paragraph (lines 58-73) is to:',
    choices: [
      { letter: 'F', text: 'describe the DNA analysis that confirmed Crocodylus niloticus and Crocodylus suchus were two distinct species.' },
      { letter: 'G', text: 'provide information on the mummification of crocodiles that was pertinent to Hekkala\'s analysis.' },
      { letter: 'H', text: 'explain how Hekkala revolutionized DNA analysis by comparing the DNA of 123 different African crocodiles.' },
      { letter: 'J', text: 'introduce the behavioral differences between Crocodylus niloticus and Crocodylus suchus.' }
    ],
    correctIndex: 0,
    correctLetter: 'F',
    explanation: 'The paragraph describes the DNA analysis that confirmed the two species were distinct.'
  },
  {
    id: '25MC1-R31',
    formId: '25MC1',
    questionNumber: 31,
    passageId: 'R4',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: true,
    question: 'According to the passage, molecular analysis revealed that immune system molecules from living crocodilian species were similar in:',
    choices: [
      { letter: 'A', text: 'structure and behavior.' },
      { letter: 'B', text: 'color and size.' },
      { letter: 'C', text: 'density and age.' },
      { letter: 'D', text: 'shape and weight.' }
    ],
    correctIndex: 0,
    correctLetter: 'A',
    explanation: 'The passage states the molecules were "remarkably similar in structure and behavior."'
  },
  {
    id: '25MC1-R32',
    formId: '25MC1',
    questionNumber: 32,
    passageId: 'R4',
    category: 'CS',
    subcategory: 'Function',
    difficulty: 2,
    isScored: true,
    question: 'In the context of the passage, the statement in lines 47-49 mainly serves to:',
    choices: [
      { letter: 'F', text: 'indicate that Klemens and his companions believed that the crocodiles were diseased.' },
      { letter: 'G', text: 'establish the tameness of the crocodiles in the desert oasis.' },
      { letter: 'H', text: 'suggest that Klemens and his companions suspected they were swimming with Crocodylus niloticus.' },
      { letter: 'J', text: 'indicate that the crocodiles in the desert oasis had not yet fully matured.' }
    ],
    correctIndex: 1,
    correctLetter: 'G',
    explanation: 'The statement about swimming beside the crocodiles "without concern" establishes how docile they were.'
  },
  {
    id: '25MC1-R33',
    formId: '25MC1',
    questionNumber: 33,
    passageId: 'R4',
    category: 'KID',
    subcategory: 'Sequence',
    difficulty: 2,
    isScored: true,
    question: 'According to the passage, after Klemens sent a tissue sample of a perished crocodile to Hekkala, Hekkala then:',
    choices: [
      { letter: 'A', text: 'estimated the crocodile\'s age.' },
      { letter: 'B', text: 'studied the crocodile\'s immune system.' },
      { letter: 'C', text: 'sequenced the crocodile\'s genome.' },
      { letter: 'D', text: 'identified mutations in the crocodile\'s molecular structure.' }
    ],
    correctIndex: 2,
    correctLetter: 'C',
    explanation: 'The passage states Hekkala "sequenced its genome" after receiving the tissue sample.'
  },
  {
    id: '25MC1-R34',
    formId: '25MC1',
    questionNumber: 34,
    passageId: 'R4',
    category: 'IKI',
    subcategory: 'Evidence',
    difficulty: 2,
    isScored: true,
    question: 'In the context of the passage, the detail that Crocodylus niloticus and Crocodylus suchus have different numbers of chromosomes provides support for the claim that the two species:',
    choices: [
      { letter: 'F', text: 'diverged during the Cretaceous.' },
      { letter: 'G', text: 'had similar diets.' },
      { letter: 'H', text: 'did not evolve from the same ancestor.' },
      { letter: 'J', text: 'were not mixing their DNA.' }
    ],
    correctIndex: 3,
    correctLetter: 'J',
    explanation: 'Different chromosome numbers support the conclusion that they "were not mixing their DNA."'
  },
  {
    id: '25MC1-R35',
    formId: '25MC1',
    questionNumber: 35,
    passageId: 'R4',
    category: 'KID',
    subcategory: 'Detail',
    difficulty: 2,
    isScored: true,
    question: 'According to the passage, Crocodylus niloticus and Crocodylus suchus diverged between:',
    choices: [
      { letter: 'A', text: '1 and 2 million years ago.' },
      { letter: 'B', text: '3 and 6 million years ago.' },
      { letter: 'C', text: '8 and 13 million years ago.' },
      { letter: 'D', text: '66 and 145 million years ago.' }
    ],
    correctIndex: 1,
    correctLetter: 'B',
    explanation: 'The passage states the two species "diverged between 3 and 6 million years ago."'
  },
  {
    id: '25MC1-R36',
    formId: '25MC1',
    questionNumber: 36,
    passageId: 'R4',
    category: 'CS',
    subcategory: 'Figurative Language',
    difficulty: 2,
    isScored: true,
    question: 'Based on the passage, the phrase "redraw the map" (line 75) is most likely meant to be read:',
    choices: [
      { letter: 'F', text: 'literally; scientists no longer believed crocodiles originated in Africa.' },
      { letter: 'G', text: 'literally; scientists no longer believed crocodiles once lived in a band of tropics.' },
      { letter: 'H', text: 'figuratively; scientists amended the narrative of the natural history of crocodiles.' },
      { letter: 'J', text: 'figuratively; scientists believed their findings would have broader implications on archaeology.' }
    ],
    correctIndex: 2,
    correctLetter: 'H',
    explanation: '"Redraw the map" is a figurative expression meaning scientists changed the understanding of crocodilian evolution.'
  }
];

// -----------------------------------------------------------------------------
// METADATA
// -----------------------------------------------------------------------------
export const metadata = {
  formId: '25MC1',
  subject: 'reading',
  source: 'Official ACT Prep Guide 2025-2026',
  totalQuestions: 36,
  scoredQuestions: 27,
  fieldTestCount: 9,
  timeLimit: 40,
  passageCount: 4,
  categories: ['KID', 'CS', 'IKI'],
  categoryBreakdown: {
    KID: 13,
    CS: 9,
    IKI: 5
  }
};

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------
export function getQuestionsByPassage(passageId) {
  return questions.filter(q => q.passageId === passageId);
}

export function getScoredQuestions() {
  return questions.filter(q => q.isScored);
}

export function getQuestionsByCategory(category) {
  return questions.filter(q => q.category === category);
}

export function calculateScaledScore(rawScore) {
  return scoringTable[rawScore] || 1;
}

console.log(`Reading Test Bank 25MC1 Loaded: ${questions.length} questions (${getScoredQuestions().length} scored)`);
